import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  ContactMethodName,
  getBackgroundColor,
  TrackerDataType,
  TABLE_REFETCH_INTERVAL_MILISEC,
} from '../../shared/constants/constants';
import dayjs from '../../utils/dayjs';
import { Calendar, dayjsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './calendar-custom-styles.css';
import { useLocation } from 'react-router-dom';
import { getCurrMonth } from '../../shared/functions/dateProcessor';
import CalendarFilter from '../TrackerFilter/CalendarFilter';
import { useMediaInteractionHandler } from '../../shared/eventHandler/MediaInteractionEventHandler';
import { useServiceLogHandler } from '../../shared/eventHandler/ServiceLogEventHandler';
import EventModal, { CalendarEvents, IssueEvent, ServiceEvent } from './EventModal';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Spinner } from 'react-bootstrap';
import { DropdownTable, FlatIssue, FlatService, Org, UserMeta } from '../../shared/types';
import { IssueKeys, ServiceKeys } from '../../shared/constants/IssueAndServiceKeys';
import { isFlatIssue, isFlatService } from '../../shared/isTypes';
import { useTheme } from '@mui/material';
import { getFilteredInteractions, getMonthlyInteractions } from '../../shared/requests/interactionsRequests';
import { useRecentCreations } from '../../shared/hooks/useRecentCreations';
import { getFilteredServices, getMonthlyServices } from '../../shared/requests/serviceNetworkRequests';
import { useFilters } from '../TrackerFilter/Context/FiltersContext';
import { getTrackerFilterQueryFromFilters } from '../TrackerFilter/trackerFilterHelpers';

const localizer = dayjsLocalizer(dayjs);

interface CalendarViewProps {
  dropdownTable: DropdownTable;
}

enum ViewType {
  MEDIA_INTERACTION = TrackerDataType.MEDIA_INTERACTION,
  SERVICE = TrackerDataType.SERVICE,
  BOTH = TrackerDataType.COMBINED_INTERACTION_AND_SERVICE,
}

const contactMethodLabelMap: Record<string, string> = {
  [ContactMethodName.NO_INTERMEDIARY]: 'in person',
  [ContactMethodName.PHONE]: 'phone call',
};

const formatContactMethod = (value?: string | null) => {
  if (!value) return '';
  return contactMethodLabelMap[value] || value;
};

function CalendarView({ dropdownTable }: Readonly<CalendarViewProps>) {
  const [selectedView, setSelectedView] = useState<ViewType>(ViewType.SERVICE); // Initialize with 'service' as the default view
  const [currMonth, setCurrMonth] = useState<string>(getCurrMonth());
  const { state } = useLocation();
  const queryClient = useQueryClient();
  const recentCreations = useRecentCreations();
  const mediaInteractionEventHandler = useMediaInteractionHandler({
    recentCreations,
    onSuccessCallback: async () => {
      await queryClient.invalidateQueries({ queryKey: ['media', currMonth] });
    },
  });
  const serviceLogEventHandler = useServiceLogHandler({
    recentCreations,
    onSuccessCallback: async () => {
      await queryClient.invalidateQueries({ queryKey: ['services', currMonth] });
    },
  });
  const [formattedEvents, setFormattedEvents] = useState<CalendarEvents>([]);
  const [selectedEvent, setSelectedEvent] = useState<IssueEvent | ServiceEvent | null>(null);
  const [isEventModalOpen, setIsEventModalOpen] = useState<boolean>(false);
  const [filteredInteractionsOrServices, setFilteredInteractionsOrServices] = useState<FlatIssue[] | FlatService[]>([]);
  const [filtersOn, setFiltersOn] = useState(false);
  const theme = useTheme();
  const filters = useFilters();

  useEffect(() => {
    setFiltersOn(false);
  }, [selectedView, setSelectedEvent]);

  const handleTabClick = (view: ViewType) => {
    setFiltersOn(false);

    setSelectedView(view);
  };

  const handleMonthChange = (newDate: Date) => {
    const newMonth = dayjs(newDate).format('YYYY-MM');
    setCurrMonth(newMonth);
  };

  const buttonStyle = {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '20px',
  };

  const tabButtonStyle = {
    padding: '10px 20px',
    backgroundColor: '#f0f0f0',
    border: 'none',
    cursor: 'pointer',
    margin: '0 5px',
    transition: 'background-color 0.3s ease',
  };

  const activeTabButtonStyle = {
    backgroundColor: getBackgroundColor(selectedView, theme),
    color: '#fff',
  };

  const handleSelectEvent = (event: IssueEvent | ServiceEvent) => {
    setSelectedEvent(event);
    setIsEventModalOpen(true);
  };

  const handleCloseEventModal = () => {
    setSelectedEvent(null);
    setIsEventModalOpen(false);
  };

  const handleDoubleClickEvent = (event: IssueEvent | ServiceEvent) => {
    setSelectedEvent(event);
    setIsEventModalOpen(true);
  };

  let month;
  if (sessionStorage.getItem('currMonth')) {
    month = sessionStorage.getItem('currMonth');
  } else {
    month = getCurrMonth();
  }

  const processFlatDataDate = (entryDate: string, secondaryDate: string | null) => {
    if (!secondaryDate) {
      return {
        startDate: dayjs(entryDate).startOf('day').toDate(),
        endDate: dayjs(entryDate).startOf('day').toDate(),
      };
    }

    return {
      startDate: dayjs(secondaryDate).startOf('day').toDate(),
      endDate: dayjs(secondaryDate).startOf('day').toDate(),
    };
  };

  const processData = useCallback(
    (data: FlatIssue[] | FlatService[], isServiceView: boolean, dropdownTable: DropdownTable): CalendarEvents => {
      if (data == null) {
        return [];
      }

      return data
        .map((item: FlatIssue | FlatService) => {
          if (!item?.cells) {
            console.error('Invalid item format');
            return null;
          }
          const secondaryDate = item.cells['Due Date'];
          const { startDate, endDate } = processFlatDataDate(item.cells['Entry Date'], secondaryDate);
          if (isServiceView && isFlatService(item)) {
            const teamMembers = item.cells[ServiceKeys.teamMember];
            const teamMemberNames = dropdownTable['lead']
              .filter((lead: UserMeta) => teamMembers.includes(lead._id))
              .map((lead: UserMeta) => lead.name);

            return {
              logtype: TrackerDataType.SERVICE,
              entryDate: dayjs(item.cells['Entry Date']).toDate(),
              dueDate: dayjs(item.cells['Due Date']).toDate(),
              start: startDate,
              end: endDate,
              background: item.cells[ServiceKeys.actions],
              title: item.cells[ServiceKeys.service],
              lead: item.cells[ServiceKeys.unitLead]
                ? item.cells[ServiceKeys.unitLead].map((expert) => expert.name).join(', ')
                : '',
              unit: item.cells[ServiceKeys.units].join(', '),
              campaign: item.cells[ServiceKeys.campaign].map((camp) => camp.name).join(', '),
              department:
                typeof item.cells[ServiceKeys.dept] === 'string'
                  ? item.cells[ServiceKeys.dept]
                  : item.cells[ServiceKeys.dept].join(', '),
              type: item.cells[ServiceKeys.type].map((type) => `${type.type}: ${type.name}`).join(', '),
              teamMembers: teamMemberNames.join(', '),
              complexity: item.cells[ServiceKeys.complexity] ? item.cells[ServiceKeys.complexity].toString() : '',
              // temporarily hidden
              // diversity: item.cells[ServiceKeys.diversity] ?? '',
              status: item.cells[ServiceKeys.status],
              keyMessaging: item.cells[ServiceKeys.keyMessaging],
              colorEvento: 'var(--service-color)',
              comms: item.cells[ServiceKeys.file] ?? [],
              id: item.id,
            };
          } else if (isFlatIssue(item)) {
            const teamMembers = item.cells[IssueKeys.lead];
            const teamMemberNames = dropdownTable['lead']
              .filter((lead: UserMeta) => teamMembers.includes(lead._id))
              .map((lead: UserMeta) => lead.name);

            const outlett = item.cells[IssueKeys.outlet];
            const outlettName = dropdownTable['outlet']?.find((org: Org) => org['_id'] === outlett);
            const journalist = dropdownTable.orgAgents[outlett ?? '']?.find?.(
              (journ) => journ._id === item.cells[IssueKeys.journalist]
            );

            return {
              logtype: TrackerDataType.MEDIA_INTERACTION,
              entryDate: dayjs(item.cells['Entry Date']).toDate(),
              dueDate: dayjs(item.cells['Due Date']).toDate(),
              start: startDate,
              end: endDate,
              title: item.cells[IssueKeys.topic],
              colorEvento: 'var(--media-color)',
              lead: item.cells[IssueKeys.expert].map((expert) => expert.name).join(', '),
              unit: item.cells[IssueKeys.units].join(', '),
              type: item.cells[ServiceKeys.type].map((type) => `${type.type}: ${type.name}`).join(', '),
              campaign: item.cells[IssueKeys.campaign].map((campaign) => campaign.name).join(', '),
              department:
                typeof item.cells[ServiceKeys.dept] === 'string'
                  ? item.cells[ServiceKeys.dept]
                  : item.cells[ServiceKeys.dept].join(', '),
              status: item.cells[IssueKeys.status],
              background: item.cells[IssueKeys.response] ?? '',
              teamMembers: teamMemberNames.join(', '),
              contact: formatContactMethod(item.cells[IssueKeys.contactMethod]),
              journalist: journalist?.name ?? '',
              keyMessaging: item.cells[IssueKeys.keyMessaging],
              outlet: outlett && outlettName ? outlettName.name : '',
              comms: item.cells[IssueKeys.file],
              id: item.id,
            };
          } else {
            return null;
          }
        })
        .filter((item: any): item is IssueEvent | ServiceEvent => item !== null)
        .sort((a: IssueEvent | ServiceEvent, b: IssueEvent | ServiceEvent) => {
          if (b.entryDate.getTime() !== a.entryDate.getTime()) {
            return b.entryDate.getTime() - a.entryDate.getTime();
          }
          return b.id.localeCompare(a.id);
        });
    },
    []
  );

  const serviceQuery = useQuery({
    queryKey: ['services', currMonth],
    queryFn: () => getMonthlyServices(currMonth, dayjs.tz.guess(), true),
    enabled: selectedView === ViewType.SERVICE || selectedView === ViewType.BOTH,
    refetchInterval:
      selectedView === ViewType.SERVICE || selectedView === ViewType.BOTH ? TABLE_REFETCH_INTERVAL_MILISEC : false,
  });

  const mediaInteractionQuery = useQuery({
    queryKey: ['media', currMonth],
    queryFn: () => getMonthlyInteractions(currMonth, dayjs.tz.guess(), true),
    enabled: selectedView === ViewType.MEDIA_INTERACTION || selectedView === ViewType.BOTH,
    refetchInterval:
      selectedView === ViewType.MEDIA_INTERACTION || selectedView === ViewType.BOTH
        ? TABLE_REFETCH_INTERVAL_MILISEC
        : false,
  });

  const serviceFiltersQueryString = useMemo(() => {
    if (!filtersOn) return '';
    return getTrackerFilterQueryFromFilters(filters, TrackerDataType.SERVICE);
  }, [filtersOn, filters]);

  const mediaFiltersQueryString = useMemo(() => {
    if (!filtersOn) return '';
    return getTrackerFilterQueryFromFilters(filters, TrackerDataType.MEDIA_INTERACTION);
  }, [filtersOn, filters]);

  const filteredServiceQuery = useQuery({
    queryKey: ['calendarFilteredServices', serviceFiltersQueryString],
    queryFn: async () => {
      const data = await getFilteredServices(serviceFiltersQueryString);
      return data || [];
    },
    enabled:
      filtersOn && !!serviceFiltersQueryString && (selectedView === ViewType.SERVICE || selectedView === ViewType.BOTH),
  });

  const filteredMediaQuery = useQuery({
    queryKey: ['calendarFilteredMedia', mediaFiltersQueryString],
    queryFn: async () => {
      const data = await getFilteredInteractions(mediaFiltersQueryString);
      return data || [];
    },
    enabled:
      filtersOn &&
      !!mediaFiltersQueryString &&
      (selectedView === ViewType.MEDIA_INTERACTION || selectedView === ViewType.BOTH),
  });

  useEffect(() => {
    setFormattedEvents([]);
    if (dropdownTable) {
      if (selectedView === ViewType.MEDIA_INTERACTION && filtersOn) {
        const source = filteredMediaQuery.data ?? filteredInteractionsOrServices;
        const formattedMediaEvents = processData(source, false, dropdownTable) as IssueEvent[];
        setFormattedEvents((formattedEvents: CalendarEvents) => [...formattedEvents, ...formattedMediaEvents]);
      } else if (
        selectedView === ViewType.MEDIA_INTERACTION &&
        mediaInteractionQuery.isSuccess &&
        mediaInteractionQuery.data
      ) {
        const formattedMediaEvents = processData(mediaInteractionQuery.data, false, dropdownTable) as IssueEvent[];
        setFormattedEvents((formattedEvents: CalendarEvents) => [...formattedEvents, ...formattedMediaEvents]);
      }

      if (selectedView === ViewType.SERVICE && filtersOn) {
        const source = filteredServiceQuery.data ?? filteredInteractionsOrServices;
        const formattedServiceEvents = processData(source, true, dropdownTable) as ServiceEvent[];
        setFormattedEvents((formattedEvents: CalendarEvents) => [...formattedEvents, ...formattedServiceEvents]);
      } else if (selectedView === ViewType.SERVICE && serviceQuery.isSuccess && serviceQuery.data) {
        const formattedServiceEvents = processData(serviceQuery.data, true, dropdownTable) as ServiceEvent[];
        setFormattedEvents((formattedEvents: CalendarEvents) => [...formattedEvents, ...formattedServiceEvents]);
      }

      if (selectedView === ViewType.BOTH && (filtersOn ? filteredServiceQuery.data : serviceQuery.data)) {
        const source = filtersOn ? filteredServiceQuery.data : serviceQuery.data;
        const formattedServiceEvents = processData(source, true, dropdownTable) as ServiceEvent[];
        setFormattedEvents((formattedEvents: CalendarEvents) => [...formattedEvents, ...formattedServiceEvents]);
      }

      if (selectedView === ViewType.BOTH && (filtersOn ? filteredMediaQuery.data : mediaInteractionQuery.data)) {
        const source = filtersOn ? filteredMediaQuery.data : mediaInteractionQuery.data;
        const formattedMediaEvents = processData(source, false, dropdownTable) as IssueEvent[];
        setFormattedEvents((formattedEvents: CalendarEvents) => [...formattedEvents, ...formattedMediaEvents]);
      }
    }
  }, [
    processData,
    selectedView,
    currMonth,
    month,
    serviceQuery.data,
    mediaInteractionQuery.data,
    filteredInteractionsOrServices,
    filteredServiceQuery.data,
    filteredMediaQuery.data,
    filtersOn,
    dropdownTable,
    mediaInteractionQuery.isSuccess,
    serviceQuery.isSuccess,
    filteredServiceQuery.isSuccess,
    filteredMediaQuery.isSuccess,
  ]);

  useEffect(() => {
    sessionStorage.setItem('currMonth', currMonth);

    if (selectedView === ViewType.MEDIA_INTERACTION) {
      mediaInteractionQuery.refetch();
    }
    if (selectedView === ViewType.SERVICE) {
      serviceQuery.refetch();
    }
  }, [currMonth, state, selectedView, setCurrMonth, month, mediaInteractionQuery, serviceQuery]);

  const handleFilteredData = (payload: any) => {
    if (Array.isArray(payload)) {
      setFilteredInteractionsOrServices(payload as FlatIssue[] | FlatService[]);
    } else {
      setFilteredInteractionsOrServices([]);
    }
  };

  return (
    <div>
      {dropdownTable ? (
        <div>
          <div>
            {selectedView !== ViewType.BOTH && (
              <CalendarFilter
                setDataCallback={handleFilteredData}
                currMonth={currMonth}
                dropdownTable={dropdownTable}
                onSelectEvent={handleSelectEvent}
                eventHandler={
                  selectedView === ViewType.MEDIA_INTERACTION ? mediaInteractionEventHandler : serviceLogEventHandler
                }
                inMediaInteraction={selectedView === ViewType.MEDIA_INTERACTION}
                typeView={selectedView}
                setFiltersOn={setFiltersOn}
                filtersOn={filtersOn}
              />
            )}
            <div style={buttonStyle} className="tabs">
              <button
                style={
                  selectedView === ViewType.SERVICE ? { ...tabButtonStyle, ...activeTabButtonStyle } : tabButtonStyle
                }
                onClick={() => handleTabClick(ViewType.SERVICE)}
              >
                Service Logs
              </button>
              <button
                style={
                  selectedView === ViewType.MEDIA_INTERACTION
                    ? { ...tabButtonStyle, ...activeTabButtonStyle }
                    : tabButtonStyle
                }
                onClick={() => handleTabClick(ViewType.MEDIA_INTERACTION)}
              >
                Media Interactions
              </button>
              <button
                style={selectedView === ViewType.BOTH ? { ...tabButtonStyle, ...activeTabButtonStyle } : tabButtonStyle}
                onClick={() => handleTabClick(ViewType.BOTH)}
              >
                All
              </button>
            </div>
          </div>
          <div>
            <div className="myCustomHeight">
              <Calendar
                localizer={localizer}
                startAccessor="start"
                endAccessor="end"
                style={{
                  height: '40vw',
                  width: '100vw',
                  padding: '8px',
                }}
                events={formattedEvents}
                views={['month', 'week', 'day']}
                defaultView={'month'}
                eventPropGetter={(myEventsList) => {
                  const backgroundColor = myEventsList.colorEvento ? myEventsList.colorEvento : 'blue';
                  return { style: { backgroundColor } };
                }}
                onNavigate={(newDate) => handleMonthChange(newDate)}
                onDoubleClickEvent={handleDoubleClickEvent}
              />

              {isEventModalOpen && <EventModal event={selectedEvent} onClose={handleCloseEventModal} />}
            </div>
          </div>
        </div>
      ) : (
        <div className="spinner-container">
          <Spinner animation="border" />
        </div>
      )}
    </div>
  );
}

export default CalendarView;
